import type { TalentCallType } from "../schemas/call.schema";
import type {
  DefinitiveEndStatus,
  MedicalComplianceStatus,
  PlanningAction,
  PlanningStatus,
  ProfessionalCardStatus,
  ResidencePermitStatus,
} from "../types/index";
import type {
  AcquisitionSource,
  ActivityStatus,
  AdvancePaymentRequestStatus,
  AmendmentType,
  ApiCaller,
  ApplicationStage,
  AppRole,
  BeneficiaryAprPaymentErrorReasons,
  BusinessLifecycleStage,
  CommercialConditionStatus,
  CommercialConditionType,
  CompanyCategory,
  ConstructionSiteSize,
  ConstructionType,
  ContractCommentType,
  ContractDocumentType,
  ContractManagementTool,
  ContractReason,
  ContractStatus,
  ContractType,
  DayOfWeek,
  DefinitiveEndSource,
  DefinitiveEndType,
  DocumentType,
  EmployeeStatus,
  ExpenseClaimRequestStatus,
  ExpenseClaimRequestType,
  ExperienceLevel,
  FactoringStatus,
  FollowingStatus,
  GenderType,
  IndependentPaymentRequestStatus,
  JobPostingStatus,
  JobRisk,
  MealCardRequestStatus,
  MedicalCenterStatus,
  MedicalCheckupStatus,
  MissionClosingReason,
  MissionStatus,
  Month,
  NightHoursSurchargeType,
  PaymentMethod,
  PayTrackingDocumentType,
  PayTrackingStatus,
  PpeRequestStatus,
  QualificationCenterStatus,
  QualificationCenterTrainingType,
  QualificationTrainingRequestDocumentType,
  QualificationTrainingRequestStatus,
  RecruitmentOfficeInvoiceRequestStatus,
  RecruitmentOfficePaymentStatus,
  ReferenceCheckStatus,
  SoftwareMasteryLevel,
  TalentQualificationStatus,
  TeamSize,
  TimesheetTransmissionType,
  TimesheetWeekStatus,
  WorkAccidentRequestStatus,
} from "../types/supabase.types.gen";
import type { TeamMemberStatus } from "../types/team-member";
import type {
  ExpenseClaimRefundSchedule,
  PldErrorField,
  Ppe,
  ProfessionalCardRefusalReason,
  Sirh,
} from "./supabase.enum";

enum Locale {
  Fr = "fr",
}

export const translateOuiNon: { [key in Locale]: { oui: string; non: string } } = {
  fr: { oui: "Oui", non: "Non" },
};

export const translateContractType: {
  [key in Locale]: { [key in ContractType]: string };
} = {
  fr: {
    interim: "Intérim",
    temporary: "CDD",
    permanent: "CDI",
    freelance: "Freelance",
  },
};

export const translateTalentCallType: {
  [key in Locale]: { [key in TalentCallType]: string };
} = {
  fr: {
    qualification: "Qualification",
    mission_proposal: "Proposition",
    mission_follow_up: "Suivi",
    timesheet_reminder: "Relance heures",
    talent_support: "Administratif",
    whats_up: "Prise de nouvelles",
    other: "Autre",
  },
};

export const translateConstructionType: {
  [key in Locale]: { [key in ConstructionType]: string };
} = {
  fr: {
    new_build: "neuf",
    rehabilitation: "réhabilitation",
    renovation: "rénovation",
    operation: "exploitation",
  },
};

export const translateApplicationStage: {
  [key in Locale]: { [key in ApplicationStage]: string };
} = {
  fr: {
    inbox: "Arrivé depuis la multidiffusion",
    shortlist: "Profil shortlisté",
    qualification_in_progress: "En cours de qualification",
    ready_to_submit: "Prêt à envoyer",
    client_review: "CV envoyé au client",
    interview: "En entretien",
    accepted: "Accepté",
  },
};

export const translateExperienceLevel: {
  [key in Locale]: { [key in ExperienceLevel]: string };
} = {
  fr: {
    from_0_to_1_year: "de 0 à 1 an",
    from_1_to_3_years: "de 1 à 3 ans",
    from_3_to_6_years: "de 3 à 6 ans",
    from_6_to_10_years: "de 6 à 10 ans",
    more_than_10_years: "plus de 10 ans",
  },
};

export const translateSoftwareMasteryLevel: {
  [key in Locale]: { [key in SoftwareMasteryLevel]: string };
} = {
  fr: {
    beginner: "novice",
    junior: "en formation",
    confirmed: "autonome",
    senior: "formateur",
    expert: "expert",
  },
};

export const translateSoftwareMasteryLevelDescription: {
  [key in Locale]: { [key in SoftwareMasteryLevel]: string };
} = {
  fr: {
    beginner: "maîtrise 0% à 10% des fonctionnalités",
    junior: "maîtrise 10% à 30% des fonctionnalités",
    confirmed: "maîtrise 30% à 60% des fonctionnalités",
    senior: "maîtrise 60% à 90% des fonctionnalités",
    expert: "maîtrise plus de 90% des fonctionnalités",
  },
};

export const translateTeamSize: {
  [key in Locale]: { [key in TeamSize]: string };
} = {
  fr: {
    less_than_10_people: "moins de 10 personnes",
    from_10_to_20_people: "de 10 à 20 personnes",
    from_20_to_50_people: "de 20 à 50 personnes",
    more_than_50_people: "plus de 50 personnes",
  },
};

export const translateConstructionSiteSize: {
  [key in Locale]: { [key in ConstructionSiteSize]: string };
} = {
  fr: {
    less_than_100K: "moins de 100k€",
    from_100K_to_500K: "de 100k à 500k€",
    from_500K_to_1M: "de 500k à 1M€",
    from_1M_to_10M: "de 1M à 10M€",
    more_than_10M: "plus de 10M€",
  },
};

export const translateGenderType: {
  [key in Locale]: { [keyof in GenderType]: string };
} = {
  fr: {
    female: "Femme",
    male: "Homme",
    neutral: "Neutre",
  },
} as const;

export const translateReferenceCheckStatus: {
  [key in Locale]: { [key in ReferenceCheckStatus]: string };
} = {
  fr: {
    old: "⚪️ Non renseignée",
    todo: "🔴 À traiter",
    done: "🟢 Effectuée",
    canceled: "⚫️ Annulée",
  },
};

export const translateRequestStatus: {
  [key in Locale]: { [key in FollowingStatus]: string };
} = {
  fr: {
    approved: "Approuvé",
    pending: "À traiter",
    canceled: "Annulé",
    refused: "Refusé",
  },
};

export const translateRecruitmentOfficeInvoiceRequestStatus: {
  [key in Locale]: { [key in RecruitmentOfficeInvoiceRequestStatus]: string };
} = {
  fr: {
    new: "Nouveau",
    pending: "Brouillon à valider",
    approved: "Approuvé",
    refused: "Refusé",
    done: "Traité",
    canceled: "Annulé",
  },
};

export const translateRecruitmentOfficePaymentStatus: {
  [key in Locale]: { [key in RecruitmentOfficePaymentStatus]: string };
} = {
  fr: {
    pending: "À payer",
    paid: "Payé",
    late: "En retard",
  },
};

export const translateMedicalCheckupRequestStatus: {
  [key in Locale]: { [key in MedicalCheckupStatus]: string };
} = {
  fr: {
    todo: "À traiter",
    pending: "En cours",
    approved: "Convoqué",
    done: "Terminé",
    canceled: "Annulé",
  },
};

export const translateMedicalComplianceStatus: {
  [key in Locale]: { [key in MedicalComplianceStatus]: string };
} = {
  fr: {
    valid: "Valide",
    processing: "En traitement",
    visit_scheduled: "Visite planifiée",
    expiring_soon: "Expire bientôt",
    expired: "Expiré",
    missing: "Manquant",
  },
};

export const translateProfessionalCardStatus: {
  [key in Locale]: { [key in ProfessionalCardStatus]: string };
} = {
  fr: {
    pending: "En cours de traitement",
    approved: "Traitée",
    refused: "Refusé",
    canceled: "Annulé",
    valid: "Valide",
    expired: "Expiré",
    expired_soon: "Expire bientôt",
    expiration_undefined: "Date d'expiration non définie",
    document_missing: "Document manquant",
    request_to_submit: "Demande à réaliser",
  },
};

export const translateQualificationTrainingRequestStatus: {
  [key in Locale]: { [key in QualificationTrainingRequestStatus]: string };
} = {
  fr: {
    todo: "À traiter",
    pending: "En cours",
    approved: "Convoqué",
    done: "Terminé",
    canceled: "Annulé",
  },
};

export const translateQualificationTrainingRequestDocumentType: {
  [key in Locale]: {
    [key in QualificationTrainingRequestDocumentType]: string;
  };
} = {
  fr: {
    invoicing: "Documents de facturation",
    convocation: "Convocation",
    certificate: "Certificat",
    other: "Autres documents",
  },
};

export type ContractStatusTranslateRoles =
  "consultant_team_member" | "accounting_team_member" | "manager_team_member" | "default";

type ContractStatusTranslation = {
  [key in ContractStatus]: {
    [key in ContractStatusTranslateRoles]: string;
  };
};

export const translateContractStatus: {
  [key in Locale]: ContractStatusTranslation;
} = {
  fr: {
    draft: {
      consultant_team_member: "Brouillon",
      accounting_team_member: "Brouillon",
      manager_team_member: "Brouillon",
      default: "Brouillon",
    },
    to_validate: {
      consultant_team_member: "En validation manager",
      accounting_team_member: "En validation",
      manager_team_member: "À valider",
      default: "En validation",
    },
    to_modify_by_owner: {
      consultant_team_member: "À modifier consultant",
      accounting_team_member: "En modification consultant",
      manager_team_member: "En modification consultant",
      default: "En modification consultant",
    },
    to_process: {
      consultant_team_member: "En traitement",
      accounting_team_member: "À traiter",
      manager_team_member: "En traitement",
      default: "En traitement",
    },
    to_modify_by_admin: {
      consultant_team_member: "En modification admin",
      accounting_team_member: "À modifier admin",
      manager_team_member: "À modifier admin",
      default: "En modification admin",
    },
    to_send: {
      consultant_team_member: "À envoyer",
      accounting_team_member: "En envoi",
      manager_team_member: "En envoi",
      default: "En envoi",
    },
    waiting_for_signature: {
      consultant_team_member: "En signature",
      accounting_team_member: "En signature",
      manager_team_member: "En signature",
      default: "En signature",
    },
    signed: {
      consultant_team_member: "Signé",
      accounting_team_member: "Signé",
      manager_team_member: "Signé",
      default: "Signé",
    },
    cancelled: {
      consultant_team_member: "Annulé",
      accounting_team_member: "Annulé",
      manager_team_member: "Annulé",
      default: "Annulé",
    },
    refused: {
      consultant_team_member: "Refusé",
      accounting_team_member: "Refusé",
      manager_team_member: "Refusé",
      default: "Refusé",
    },
  },
};

export const translateMedicalCenterStatus: {
  [key in Locale]: { [key in MedicalCenterStatus]: string };
} = {
  fr: {
    pending: "En cours",
    approved: "Validé",
    refused: "Refusé",
  },
};

export const translateMedicalCenterOption: {
  [key in Locale]: { [key in Exclude<MedicalCenterStatus, "pending">]: string };
} = {
  fr: {
    approved: "✅ Valider",
    refused: "❌ Refuser",
  },
};

export const translateQualificationCenterOption: {
  [key in Locale]: {
    [key in Exclude<QualificationCenterStatus, "pending">]: string;
  };
} = {
  fr: {
    approved: "✅ Valider",
    refused: "❌ Refuser",
  },
};
export const translateQualificationCenterStatus: {
  [key in Locale]: { [key in QualificationCenterStatus]: string };
} = {
  fr: {
    pending: "En cours",
    approved: "Validé",
    refused: "Refusé",
  },
};

export const translateQualificationCenterTrainingType: {
  [key in Locale]: { [key in QualificationCenterTrainingType]: string };
} = {
  fr: {
    remote: "Remote",
    "in-person": "Présentiel",
    hybrid: "Hybride",
  },
};
export const translateAdvancePaymentRequestStatus: {
  [key in Locale]: { [key in AdvancePaymentRequestStatus]: string };
} = {
  fr: {
    approved: "Traité",
    paid: "Payé",
    pending: "À traiter",
    pending_transfer_confirmation: "En cours",
    canceled: "Annulé",
    refused: "Refusé",
    error: "Erreur",
  },
};

export const translateIndependentPaymentRequestStatus: {
  [key in Locale]: { [key in IndependentPaymentRequestStatus]: string };
} = {
  fr: {
    pending: "À traiter",
    approved: "Approuvé",
    refused: "Refusé",
    paid: "Payé",
    canceled: "Annulé",
  },
};

export const translateJobRisk: {
  [key in Locale]: { [key in JobRisk]: string };
} = {
  fr: {
    burial_or_sinking: "Risques de noyade ou ensevelissement",
    dust_treatments_milling: "Poussières, traitements, fraisage",
    fall_from_height: "Chute de hauteur",
    handling_heavy_objects: "Manutention d'objets lourds",
    manual_handling: "Manutention manuelle",
    demolition: "Risques de démolition",
    electrical_risks: "Risques électriques",
    use_of_chemical_products: "Risques chimiques",
    use_of_dangerous_tools: "Utilisation d'outils dangereux",
    working_under_traffic: "Travail sur voie de circulation",
  },
};

export const translateEmployeeStatus: {
  [key in Locale]: { [key in EmployeeStatus]: string };
} = {
  fr: {
    executive: "Cadre",
    technician: "ETAM",
    worker: "Ouvrier",
  },
};

export const translateNightHoursSurchargeType: {
  [key in Locale]: { [key in NightHoursSurchargeType]: string };
} = {
  fr: {
    occasional: "Ponctuel",
    exceptional: "Exceptionnel",
  },
};

export const translateContractReason: {
  [key in Locale]: { [key in ContractReason]: string };
} = {
  fr: {
    increased_company_actvity: "Accroissement temporaire d'activité",
    employee_replacement: "Remplacement d’un employé",
    talent_professional_training: "Formation professionnelle de l’intérimaire",
  },
};

export const translatePpe: {
  [key in Locale]: { [key in Ppe]: string };
} = {
  fr: {
    safety_shoes: "Chaussures de sécurité",
    hearing_protection_helmet: "Casque anti-bruit",
    protective_glasses: "Lunettes de protection",
    protective_gloves: "Gants de protection",
    safety_helmet: "Casque de sécurité",
    safety_vest: "Gilet de sécurité",
    safety_pants: "Pantalon de sécurité",
    comfort_parka: "Manteau/Parka",
    comfort_polar_vest: "Veste polaire",
    comfort_earplugs: "Bouchons d'oreille",
  },
};

export const translateMonth: {
  [key in Locale]: { [key in Month]: string };
} = {
  fr: {
    january: "Janvier",
    february: "Février",
    march: "Mars",
    april: "Avril",
    may: "Mai",
    june: "Juin",
    july: "Juillet",
    august: "Août",
    september: "Septembre",
    october: "Octobre",
    november: "Novembre",
    december: "Décembre",
  },
};

const MONTHS_IN_ORDER: Month[] = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

/** "01 Juillet" — the day-and-month label French e-mail headers use. */
export const formatDayAndMonthFr = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = MONTHS_IN_ORDER.at(date.getMonth());
  return month === undefined ? day : `${day} ${translateMonth.fr[month]}`;
};

export type SinceOptions = "one_month" | "three_months" | "six_months" | "forever";

export const translateTimesheetSinceOption: {
  [key in Locale]: { [key in SinceOptions]: string };
} = {
  fr: {
    one_month: "1 mois",
    three_months: "3 mois",
    six_months: "6 mois",
    forever: "toujours",
  },
};

export const translateDayOfWeek = function (day: DayOfWeek) {
  const map: { [key in DayOfWeek]: string } = {
    monday: "lundi",
    tuesday: "mardi",
    wednesday: "mercredi",
    thursday: "jeudi",
    friday: "vendredi",
    saturday: "samedi",
    sunday: "dimanche",
  };

  return map[day];
};

export const translateAmendmentType: {
  [key in Locale]: { [key in AmendmentType]: string };
} = {
  fr: {
    extension: "prolongation",
    modification: "modificatif",
  },
};

export const translateDocumentType: {
  [key in Locale]: { [key in DocumentType]: string };
} = {
  fr: {
    admin__banking__rib: "RIB",
    admin__health__medical_certificate: "Certificat médical",
    admin__health__medical_convocation: "Convocation médicale",
    professional__reference_letter: "Lettre de recommandation",
    professional__qualification_certificate: "Habilitation",
    professional__professional_card_btp: "Carte BTP",
    professional__driving_licence: "Permis de conduire",
    professional__cover_letter: "Lettre de motivation",
    other__misc: "Autre",
    other__legal__criminal_record: "Casier judiciaire",
    other__company_insurance__response: "Attestation d'assurance",
    admin__qualification_training_request: "Demande d'habilitation",
    admin__residence__proof_of_identity_of_the_host: "Justificatif d'identité de l'hébergeur",
    admin__residence__proof_of_address_of_the_host: "Justificatif de domicile de l'hébergeur",
    admin__residence__proof_of_address: "Justificatif de domicile",
    admin__residence__accomodation_certificate: "Attestation d'hébergement",
    admin__identity__residence_permit: "Titre de séjour",
    admin__identity__passeport: "Passeport",
    admin__identity__id_card: "Carte d'identité",
    admin__health_insurance__vital_card: "Carte vitale",
    admin__health_insurance__social_security_certificate: "Attestation de sécurité sociale",
    admin__health_insurance__european_health_insurance_card: "Carte européenne d'assurance maladie",
    contract__recruitment_office__invoice_draft: "Brouillon de facture du cabinet de recrutement",
    contract__recruitment_office__invoice_real: "Facture finale du cabinet de recrutement",
    admin__contract__document: "Document lié au contrat",
    admin__health__work_accident_work_interruption_certificate: "Certificat d'arrêt de travail",
    admin__health__work_accident_submission_certificate: "Attestation de dépôt de dossier d'accident du travail",
    admin__health__work_accident_health_insurance_declaration: "Déclaration d'accident de travail à la CPAM",
    admin__health__work_accident_health_insurance_submission:
      "Attestation de dépôt de déclaration de l'accident de travail à la CPAM",
    admin__health__work_accident_professional_disease_form:
      "Feuille d'accident de travail ou de maladie professionnelle",
    admin__health__work_accident_user_company_declaration:
      "Déclaration d'accident de travail de l'entreprise utilisatrice",
    admin__health__work_accident_health_insurance_validation:
      "Décision de la CPAM relatif à la validation de l'accident de travail",
    admin__health__work_accident_health_insurance_clarification:
      "Formulaire de demande de clarification par la CPAM relatif à la déclaration de l'accident de travail",
    admin__health__work_accident_other: "Autre",
    admin__health__medical_leave_health_insurance_declaration: "Déclaration d'arrêt de travail à la CPAM",
    admin__health__medical_leave_daily_allowances_salary:
      "Attestation de salaire pour le paiement des indemnités journalières",
    admin__health__medical_leave_health_insurance_submission: "Attestation de dépôt de l'arrêt de travail à la CPAM",
    admin__health__medical_leave_supplementary_insurance_submission:
      "Attestation de dépôt de l'arrêt de travail à la Mutuelle Intérim Prévoyance",
    contract__recruitment_office__payment: "Paiement de la demande cabinet de recrutement",
    contract__recruitment_office__work_order: "Bon de commande",
    admin__pay_tracking: "Document lié à la paie",
    admin__payslip: "Bulletin de paie",
    admin__attestation: "Attestation de travail",
    admin__certificate: "Certificat France Travail",
    admin__company_invoice: "Facture",
    admin__company_credit: "Crédit",
    admin__company_remuneration_reference: "Document de référence rémunération",
    admin__company_other: "Autre",
    admin__company_temporary_invoice: "Facture temporaire",
    contract__summary: "Document de synthèse",
  },
};

export const translateMissionJobPostingStatus: {
  [key in Locale]: { [key in JobPostingStatus]: string };
} = {
  fr: {
    archived: "Archivé",
    draft: "Brouillon",
    published: "Publié",
  },
};

export const translateCommercialConditionType: {
  [key in Locale]: { [key in CommercialConditionType]: string };
} = {
  fr: {
    interim: "Intérim",
    recruitment_office: "Cabinet de recrutement",
    interim_and_recruitment_office: "Intérim et cabinet de recrutement",
  },
};

export const translateDefinitiveEndType: {
  [key in Locale]: { [key in DefinitiveEndType]: string };
} = {
  fr: {
    administrative_reason: "SHOULD_NOT_BE_USED_V1",
    disciplinary_reason: "SHOULD_NOT_BE_USED_V1",
    early_resignation: "SHOULD_NOT_BE_USED_V1",
    talent_end_of_trial_period: "SHOULD_NOT_BE_USED_V1",
    sick_leave: "Arrêt Maladie",
    found_permanent_job_elsewhere: "A trouvé un CDI ailleurs",
    technical_bug: "Bug technique",
    worker_leave: "Congés intérimaire",
    permanent_hire: "Embauche CDI - autre client d'asap.work",
    permanent_hire_by_current_client: "Embauche CDI par le client actuel",
    end_of_mission_by_worker: "Fin de mission à l’initiative de l’intérimaire",
    end_of_mission: "Fin de chantier et pas de mission prévue pour la suite",
    competition_flip_flop: "Flipflop par la concurrence",
    not_qualified: "Pas au niveau",
    no_show: "Plantage",
    talent_no_show: "Plantage par le candidat",
    client_no_show: "Plantage Client",
    contract_renewal: "Renouvellement de contrat",
    negative_flexibility: "Souplesse négative",
    positive_flexibility: "Souplesse positive",
    other: "Autre",
    contract_new: "Nouveau contrat avec le même client (hors renouvellement)",
    contract_new_with_different_client: "Nouveau contrat avec un autre client",
    end_of_mission_by_client: "Fin de mission à l’initiative du client",
    will_no_longer_work_with_us: "Ne travaillera plus avec nous (comportement, problème administratif…)",
    end_of_trial_period: "Fin de période d’essai",
  },
};

export const translateDefinitiveEndTypeV2: {
  [key in Locale]: { [key in DefinitiveEndType]: string };
} = {
  fr: {
    no_show: "No-show",
    negative_flexibility: "Souplesse négative",
    positive_flexibility: "Souplesse positive",
    end_of_mission: "Fin de mission sans remplacement",
    administrative_reason: "Administratif / conformité",
    contract_new: "Fin de mission avec nouveau contrat",
    talent_end_of_trial_period: "Rupture de période d'essai",
    disciplinary_reason: "Raisons disciplinaires / comportement",
    permanent_hire_by_current_client: "Embauche par l’Entreprise Utilisatrice",
    end_of_trial_period: "Rupture de période d’essai (insatisfaction du client)",
    early_resignation: "Démission - avant échéance du contrat et dépassement de la période d'essai",
    other: "Autre",
    sick_leave: "SHOULD_NOT_BE_USED_V2",
    worker_leave: "SHOULD_NOT_BE_USED_V2",
    not_qualified: "SHOULD_NOT_BE_USED_V2",
    technical_bug: "SHOULD_NOT_BE_USED_V2",
    talent_no_show: "SHOULD_NOT_BE_USED_V2",
    client_no_show: "SHOULD_NOT_BE_USED_V2",
    permanent_hire: "SHOULD_NOT_BE_USED_V2",
    contract_renewal: "SHOULD_NOT_BE_USED_V2",
    competition_flip_flop: "SHOULD_NOT_BE_USED_V2",
    end_of_mission_by_client: "SHOULD_NOT_BE_USED_V2",
    end_of_mission_by_worker: "SHOULD_NOT_BE_USED_V2",
    will_no_longer_work_with_us: "SHOULD_NOT_BE_USED_V2",
    found_permanent_job_elsewhere: "SHOULD_NOT_BE_USED_V2",
    contract_new_with_different_client: "SHOULD_NOT_BE_USED_V2",
  },
};

export const translateDefinitiveEndSource: {
  [key in Locale]: { [key in DefinitiveEndSource]: string };
} = {
  fr: {
    admin: "À notre initiative",
    client: "À l’initiative du client",
    talent: "À l’initiative de l’intérimaire",
    neutral: "À la date prévue du contrat",
  },
};

export const translatePlanningStatus: {
  [key in Locale]: { [key in PlanningStatus]: string };
} = {
  fr: {
    upcoming: "À venir",
    current: "En cours",
    late: "En retard",
    done: "Terminé",
    almost_done: "Fin cette semaine",
    upcoming_next_week: "À venir semaine prochaine",
    upcoming_this_week: "À venir cette semaine",
  },
};

export const translateTimesheetWeek = (status: TimesheetWeekStatus) => {
  switch (status) {
    case "fully_approved":
      return "Signé";
    case "completed_by_talent":
      return "En attente de validation";
    case "not_completed":
      return "Non complété";
    case "rejected_by_client":
      return "Modifié par le client";
    case "rejected_by_talent":
      return "Modifié par le talent";
    case "completed_by_client":
      return "Validé par le client";
    default:
      return "Should not happen";
  }
};

export const translatePlanningAction = (action: PlanningAction) => {
  switch (action) {
    case "contract_details":
      return "👀 Détails du contrat";
    case "definitive_end":
      return "👋 Fin définitive";
    case "mark_as_signed":
      return "✍️ Marquer comme signé";
    case "create_amendment":
      return "👉 Créer un avenant";
  }
};

export const translateTalentQualificationStatus: {
  [key in Locale]: { [key in TalentQualificationStatus | "dereferenced"]: string };
} = {
  fr: {
    not_qualified: "Non qualifié",
    pre_qualified: "Pré-qualifié",
    qualification_in_progress: "En cours",
    qualified: "Qualifié",
    disqualified: "Disqualifié",
    dereferenced: "Déréférencé",
  },
};

export const translateCommercialConditionStatus: {
  [key in Locale]: { [key in CommercialConditionStatus]: string };
} = {
  fr: {
    not_signed: "À envoyer",
    to_validate: "À valider",
    signed: "Signé",
    refused: "Refusé",
    canceled: "Annulé",
    sent: "Envoyé",
  },
};

export const translateMissionStatus: {
  [key in Locale]: { [key in MissionStatus]: string };
} = {
  fr: {
    open: "Ouverte",
    closed: "Fermée",
  },
};

export const translateMissionClosingReason: {
  [key in Locale]: { [key in MissionClosingReason]: string };
} = {
  fr: {
    completed_by_client: "Pourvue par le client",
    completed_by_competition: "Pourvue par la concurrence",
    completed: "Pourvue par asap.work",
    canceled_by_client: "Annulée par le client",
    canceled: "Annulée par le consultant",
    too_old: "Date de début trop ancienne",
  },
};

export const translateMissionPriority: {
  [key in Locale]: { [key: number]: string };
} = {
  fr: {
    0: "Urgente",
    1: "Élevée",
    2: "Moyenne",
    3: "Faible",
    4: "-",
  },
};

export const translateDefinitiveEndStatus: {
  [key in Locale]: { [key in DefinitiveEndStatus]: string };
} = {
  fr: {
    all: "Tous",
    to_process: "À traiter",
    done: "Traité",
  },
};

export const translateAcquisitionSources: {
  [key in Locale]: { [key in AcquisitionSource]: string };
} = {
  fr: {
    website: "Site internet",
    prospecting: "Prospection",
    event: "Événement",
    other: "Autre",
    apec__multidiffusion: "Apec (Multidiffusion)",
    apec__sourcing: "Apec (Sourcing)",
    b2b_transformation: "Transformation B2B",
    beetween__import: "Beetween (Import)",
    cadremploi__multidiffusion: "Cadremploi (Multidiffusion)",
    direct_marketing__email: "Marketing Direct (Email)",
    direct_marketing__push: "Marketing Direct (Push)",
    direct_marketing__sms: "Marketing Direct (Sms)",
    direct_marketing__whatsapp: "Marketing Direct (Whatsapp)",
    emploi_interim__multidiffusion: "Emploi Interim (Multidiffusion)",
    facebook_ads__carousel: "Pub Facebook (Carousel)",
    facebook_ads__image: "Pub Facebook (Image)",
    facebook_ads__lead: "Pub Facebook (Lead)",
    facebook_ads__video: "Pub Facebook (Vidéo)",
    figaro_emploi__multidiffusion: "Figaro Emploi (Multidiffusion)",
    google_ads__display: "Google Ads (Display)",
    google_ads__search: "Google Ads (Search)",
    google_ads__video: "Google Ads (Vidéo)",
    hellowork__multidiffusion: "Hellowork (Multidiffusion)",
    hellowork__researcher: "Hellowork (Scrapping Bot)",
    hellowork__sourcing: "Hellowork (Sourcing)",
    hubspot__import: "Hubspot (Import)",
    indeed__multidiffusion: "Indeed (Multidiffusion)",
    inrecruiting__import: "Inrecruiting (Import)",
    linkedin__sourcing: "Linkedin (Sourcing)",
    linkedin_ads__carousel: "LinkedIn Ads (Carousel)",
    linkedin_ads__image: "LinkedIn Ads (Image)",
    linkedin_ads__lead: "LinkedIn Ads (Lead)",
    linkedin_ads__text: "LinkedIn Ads (Texte)",
    linkedin_ads__video: "LinkedIn Ads (Vidéo)",
    meteojob__multidiffusion: "Meteojob (Multidiffusion)",
    meteojob__researcher: "Meteojob (Scrapping Bot)",
    ordre_des_architectes__multidiffusion: "Ordre Des Architectes (Multidiffusion)",
    pole_emploi__multidiffusion: "France Travail (Multidiffusion)",
    pole_emploi__researcher: "France Travail (Scrapping Bot)",
    pole_emploi__sourcing: "France Travail (Sourcing)",
    social_media__facebook: "Compte Facebook asap.work",
    social_media__instagram: "Compte Instagram asap.work",
    social_media__linkedin: "Compte Linkedin asap.work",
    social_media__tiktok: "Compte Tiktok asap.work",
    social_media__youtube: "Compte Youtube asap.work",
    sponsoring: "Sponsoring",
    co_marketing: "Co-Marketing",
    newsletter: "Newsletter",
    referral: "Recommandation",
    emailing: "Campagne Email",
    cvaden__sourcing: "Cvaden (Sourcing)",
    meteojob__sourcing: "Meteojob (Sourcing)",
    linkedin__multidiffusion: "Linkedin (Multidiffusion)",
    directemploi__multidiffusion: "Directemploi (Multidiffusion)",
    other__multidiffusion: "Autre (Multidiffusion)",
    facebook__sourcing: "Facebook (Sourcing)",
    facebook__marketplace: "Facebook (Marketplace)",
    facebook__multidiffusion: "Facebook (Multidiffusion)",
    portage__sourcing: "Portage (Sourcing)",
    hellowork_gratuit__multidiffusion: "Hellowork Gratuit (Multidiffusion)",
    leboncoin__multidiffusion: "Leboncoin (Multidiffusion)",
    jooble__multidiffusion: "Jooble (Multidiffusion)",
    menco__import: "Menco (Import)",
  },
};

export const translateApiCaller: {
  [key in Locale]: { [key in ApiCaller]: string };
} = {
  fr: {
    app__back_office: "Backoffice",
    app__researcher: "Robot chercheur",
    app__talent: "App mobile",
    hellowork__ats_connect: "HelloWork",
    legacy__xano: "Xano",
    make__facebook_ads__lead: "Facebook Ads",
    make__multidiffusion__broadbeen: "Broadbeen",
    make__multidiffusion__talentplug: "Talentplug",
    make__website__form: "Site web",
    sync__inrecruiting: "Inrecruiting",
  },
};

export const translateExpenseClaimRequestType: {
  [key in Locale]: { [key in ExpenseClaimRequestType]: string };
} = {
  fr: {
    ppe: "EPI",
    transportation: "Transports",
    mileage: "Kilométrage",
    tooling: "Outillage",
    food: "Repas",
    accommodation: "Hébergement",
    travel: "Déplacements",
  },
};

export const translateExpenseClaimRequestRefundSchedule: {
  [key in Locale]: { [key in ExpenseClaimRefundSchedule]: string };
} = {
  fr: {
    immediately: "Immédiat",
    at_next_payroll: "Prochaine paie",
  },
};

export const translateNullExpenseClaimRequestType: {
  [key in Locale]: string;
} = {
  fr: "Non Renseigné",
};

export type ExpenseClaimRequestStatusRoleTranslation =
  "consultant_team_member" | "accounting_team_member" | "manager_team_member" | "default";
type ExpenseClaimRequestStatusTranslation = {
  [key in ExpenseClaimRequestStatus]: {
    [key in ExpenseClaimRequestStatusRoleTranslation]: string;
  };
};
export const translateExpenseClaimRequestStatus: {
  [key in Locale]: ExpenseClaimRequestStatusTranslation;
} = {
  fr: {
    to_review: {
      consultant_team_member: "À confirmer",
      accounting_team_member: "En confirmation",
      manager_team_member: "En confirmation",
      default: "En confirmation",
    },
    to_validate: {
      consultant_team_member: "En validation",
      accounting_team_member: "En validation",
      manager_team_member: "À valider",
      default: "En validation",
    },
    pending: {
      consultant_team_member: "En traitement",
      accounting_team_member: "À traiter",
      manager_team_member: "En traitement",
      default: "En traitement",
    },
    approved: {
      consultant_team_member: "Approuvé",
      accounting_team_member: "Approuvé",
      manager_team_member: "Approuvé",
      default: "Approuvé",
    },
    refused: {
      consultant_team_member: "Refusé",
      accounting_team_member: "Refusé",
      manager_team_member: "Refusé",
      default: "Refusé",
    },
    canceled: {
      consultant_team_member: "Annulé",
      accounting_team_member: "Annulé",
      manager_team_member: "Annulé",
      default: "Annulé",
    },
    error: {
      consultant_team_member: "Erreur",
      accounting_team_member: "Erreur",
      manager_team_member: "Erreur",
      default: "Erreur",
    },
  },
};

export const translatePpeRequestStatus: {
  [key in Locale]: { [key in PpeRequestStatus]: string };
} = {
  fr: {
    to_validate: "À confirmer",
    todo: "À traiter",
    approved: "Approuvé",
    pending: "En cours",
    refused: "Refusé",
  },
};

export const translateContractDocumentType: {
  [key in Locale]: { [key in ContractDocumentType]: string };
} = {
  fr: {
    pre_employment_declaration: "Déclaration préalable à l'embauche",
    contract_client: "Contrat client non signé",
    contract_talent: "Contrat talent non signé",
    contract_client_signed: "Contrat client signé",
    contract_talent_signed: "Contrat talent signé",
    other: "Autre",
  },
};

export const translateAppRole: {
  [key in Locale]: { [key in AppRole]: string };
} = {
  fr: {
    accounting_team_member: "Comptabilité",
    consultant_team_member: "Consultant",
    csm_team_member: "WSM",
    super_team_member_deprecated: "Super Team Member (déprécié)",
    talent: "Talent",
    team_member: "Membre",
    team_member_developer: "Tech",
    team_member_manager_deprecated: "Manager (déprécié)",
    team_member_marketing: "Marketing",
    team_member_independent_administrator: "Référent indépendant",
    external_csm_team_member: "WSM externe",
    finance_team_member: "Finance",
  },
};

export const translateResidencePermitStatus: {
  [key in Locale]: { [key in ResidencePermitStatus]: string };
} = {
  fr: {
    valid: "Valide",
    expired: "Expiré",
    expired_soon: "Expire bientôt",
    expiration_undefined: "Date d'expiration non définie",
    document_to_upload: "Document manquant",
  },
};

export const translateWorkAccidentRequestStatus: {
  [key in Locale]: { [key in WorkAccidentRequestStatus]: string };
} = {
  fr: {
    todo: "À traiter",
    pending: "En cours",
    done: "Terminé",
    canceled: "Annulé",
  },
};

type ManagementLevel = 1 | 2 | 3 | 4;

export const translateManagementLevel: {
  [key in Locale]: { [key in ManagementLevel]: string };
} = {
  fr: {
    1: "Consultant / Autres",
    2: "Business Unit Manager",
    3: "Secteur Manager",
    4: "Direction",
  },
};

type PayTrackingStatusTranslation = {
  [key in PayTrackingStatus]: {
    [key in "accounting_team_member" | "consultant_team_member" | "default"]: string;
  };
};

export const translatePayTrackingStatus: {
  [key in Locale]: PayTrackingStatusTranslation;
} = {
  fr: {
    to_complete: {
      consultant_team_member: "À compléter",
      accounting_team_member: "En complétion",
      default: "En complétion",
    },
    to_process: {
      consultant_team_member: "En traitement",
      accounting_team_member: "À traiter",
      default: "En traitement",
    },
    to_verify: {
      consultant_team_member: "À vérifier",
      accounting_team_member: "En vérification",
      default: "En vérification",
    },
    proceed: {
      consultant_team_member: "Traitée",
      accounting_team_member: "Traitée",
      default: "Traitée",
    },
    canceled: {
      consultant_team_member: "Annulée",
      accounting_team_member: "Annulée",
      default: "Annulée",
    },
    waiting_payroll_document: {
      consultant_team_member: "En attente",
      accounting_team_member: "Documents à fournir",
      default: "En attente",
    },
  },
};

export const translateHRISName: {
  [key in Locale]: { [key in Sirh]: string };
} = {
  fr: {
    baps: "BAPS",
    peopulse: "Peopulse",
    pixid: "Pixid",
    by_hand: "RH papier",
    pilott: "PilOTT",
  },
};

export const translateTimesheetTransmissionType: {
  [key in Locale]: { [key in TimesheetTransmissionType]: string };
} = {
  fr: {
    app: "APP",
    paper: "PAPIER",
  },
};

export const translateTimesheetMethodLabel = (label: TimesheetTransmissionType | Sirh): string => {
  if (label === "app" || label === "paper") return translateTimesheetTransmissionType.fr[label];
  return translateHRISName.fr[label];
};

export const translatePayTrackingDocumentType: {
  [key in Locale]: { [key in PayTrackingDocumentType]: string };
} = {
  fr: {
    pay_slip: "Bulletin de paie",
    pay_slip_final: "Bulletin de paie définitif",
    invoice: "Facture",
    other: "Autre",
    attestation: "Attestation de travail",
    certificate: "Certificat France Travail",
    temporary_invoice: "Facture temporaire",
  },
};

export const translateMealCardRequestStatus: {
  [key in Locale]: { [key in MealCardRequestStatus]: string };
} = {
  fr: {
    to_process: "À traiter",
    processed: "Traité",
  },
};

export const translateTeamMemberStatus: {
  [key in Locale]: { [key in TeamMemberStatus]: string };
} = {
  fr: {
    all: "Tous",
    permanent: "Salariés",
    independent: "Indépendants",
  },
};

export const translateContractManagementTool: {
  [key in Locale]: { [key in ContractManagementTool]: string };
} = {
  fr: {
    all: "Tous",
    sirh: "SIRH",
    app: "APP",
  },
};

export const translateBusinessLifecycleStage: {
  [key in Locale]: { [key in BusinessLifecycleStage]: string };
} = {
  fr: {
    prospect: "Prospect",
    lead: "Lead",
    client: "Client",
  },
};

export const translateContractCommentFromType: {
  [key in Locale]: { [key in ContractCommentType]: string };
} = {
  fr: {
    manager_to_owner: "Manager",
    admin_to_owner: "Admin",
    owner_to_admin: "Consultant",
  },
};

export const translateBeneficiaryTransferErrorReasons: {
  [key in Locale]: { [key in BeneficiaryAprPaymentErrorReasons]: string };
} = {
  fr: {
    instant_transfer_not_available: "Le bénéficiaire ne peut pas recevoir de virements instantanés",
    invalid_beneficiary_iban: "L'IBAN du bénéficiaire est invalide",
    invalid_iban_format: "Le format de l'IBAN du bénéficiaire est invalide",
    unreachable_beneficiary_iban: "Le bénéficiaire est injoignable pour ce type de virement",
    maximum_amount_exceeded: "Le montant du virement dépasse la limite",
    beneficiary_bank_account_closed: "Le compte bancaire du bénéficiaire est clôturé",
    beneficiary_bank_error: "La banque du bénéficiaire nous a envoyé une erreur",
    beneficiary_bank_invalid_bank_details: "Le compte bancaire du bénéficiaire n'existe pas ou n'existe plus",
    beneficiary_bank_refusal: "La banque du bénéficiaire a refusé le virement",
    intermediary_system_error: "Le réseau interbancaire nous a envoyé une erreur",
  },
};

export const translatePldErrorField: {
  [key in Locale]: { [key in PldErrorField]: string };
} = {
  fr: {
    internal_pld_talent_id_missing: "Talent ID externe manquant: PLD",
    client_not_found: "Client non trouvé dans PLD",
    multiple_clients_found: "Plusieurs clients trouvés dans PLD",
    contract_not_found: "Contrat non trouvé dans PLD",
    failed_to_isolate_contract: "Échec de l'isolement du contrat dans PLD",
    empty_week: "Semaine envoyée vide",
    week_already_in_pld: "Semaine déjà dans PLD",
    extension_send_failed: "Échec de l'envoi de la prolongation à PLD",
    extension_send_conflict: "Cette prolongation existe déjà dans PLD",
    extension_max_reached: "Nombre maximum de prolongations atteint",
    extension_pld_target_unresolved: "Contrat PLD cible introuvable pour la prolongation",
  },
};

import type { SocialSecurityNumberType } from "./french-social-security-number";

export const translateSocialSecurityNumberType: {
  [key in Locale]: { [key in SocialSecurityNumberType]: string };
} = {
  fr: {
    NIR: "NIR",
    NIA: "NIA",
    NTT: "NTT",
  },
};

export const translateSocialSecurityNumberTypeDescription: {
  [key in Locale]: { [key in SocialSecurityNumberType]: string };
} = {
  fr: {
    NIR: "Numéro d'Inscription au Répertoire - Le numéro de sécurité sociale standard français",
    NIA: "Numéro d'Identification d'Attributaire - Attribué aux personnes sans NIR",
    NTT: "Numéro Temporaire de Travailleur - Pour les travailleurs temporaires",
  },
};

export const translatePaymentMethod: {
  [key in Locale]: { [key in PaymentMethod]: string };
} = {
  fr: {
    bank_transfer: "Virement bancaire",
    check: "Chèque",
    bill_of_exchange: "Lettre de change",
    other: "Autre",
  },
};

export const translateFactoringStatus: {
  [key in Locale]: { [key in FactoringStatus]: string };
} = {
  fr: {
    active: "Factor",
    inactive: "Non Factor",
  },
};

export const translateActivityStatus: {
  [key in Locale]: { [key in ActivityStatus]: string };
} = {
  fr: {
    active: "Active",
    inactive: "Inactive",
    ceased: "Cessée",
  },
};

export const translateCompanyCategory: {
  [key in Locale]: { [key in CompanyCategory]: string };
} = {
  fr: {
    large: "GE",
    medium: "ETI",
    small: "PME",
    very_small: "TPE",
  },
};

export const translateProfessionalCardRefusalReason: {
  [key in Locale]: { [key in ProfessionalCardRefusalReason]: string };
} = {
  fr: {
    non_compliant_photo: "Photo non conforme",
    incorrect_or_unreadable_document: "Document incorrect / illisible",
    invalid_card: "Carte invalide",
    other: "Autre",
  },
};
